import React, { FC } from 'react'
import classnames from 'classnames'
import { Flex, Slider, Typography } from 'antd'
import type { SliderProps as RcSliderProps } from '@rc-component/slider'
import { RankingWeights } from './vespa-strategy-presets'
import './vespa-ranking-weights.scss'

const { Text } = Typography

const WEIGHT_MARKS: RcSliderProps['marks'] = {
    0: 'Off',
    25: 'Low',
    50: 'Med',
    75: 'High',
    100: 'Max',
}

type VespaRankingWeightsProps = {
    weights: RankingWeights
    onChange: (weights: RankingWeights) => void
    readonly?: boolean
}

/**
 * Ranking weight dials for Personalized and Recent. Each dial exposes the five
 * discrete levels Off→Max and reports the selected level back to the caller as
 * a 0–4 dial stop.
 */
export const VespaRankingWeights: FC<VespaRankingWeightsProps> = ({ weights, onChange, readonly }) => {
    return (
        <Flex gap="var(--space-3)" wrap="wrap">
            <WeightDial
                label="Personalized"
                value={weights.personalized}
                onChange={(v) => onChange({ ...weights, personalized: v })}
                readonly={readonly}
            />
            <WeightDial
                label="Recent"
                value={weights.recent}
                onChange={(v) => onChange({ ...weights, recent: v })}
                readonly={readonly}
            />
        </Flex>
    )
}

type WeightDialProps = {
    label: string
    value: number
    onChange: (value: number) => void
    readonly?: boolean
}

const WeightDial: FC<WeightDialProps> = ({ label, value, onChange, readonly }) => {
    return (
        <div className="vespa-weight-dial">
            <Text strong className="vespa-weight-dial__label">
                {label}
            </Text>
            <Slider
                classNames={{
                    root: classnames('vespa-weight-dial__slider', `vespa-weight-dial__slider-${value}`),
                    tracks: 'vespa-weight-dial__tracks',
                    track: 'vespa-weight-dial__track',
                    rail: 'vespa-weight-dial__rail',
                    handle: 'vespa-weight-dial__handle',
                }}
                ariaLabelForHandle={`${label} weight`}
                marks={WEIGHT_MARKS}
                value={(value * 100) / 4}
                onChange={(v) => onChange((v / 100) * 4)}
                step={25}
                disabled={readonly}
                tooltip={{ open: false }}
            />
        </div>
    )
}
